import { Injectable, Renderer2, RendererFactory2 } from "@angular/core";
import { TranslocoService } from "@jsverse/transloco";
import { AppInjector } from "app/app.module";
import { DomHandlersService } from "app/global/dom-handlers.service";
import { GlobalService } from "app/global/global.service";
import { Block } from "app/interfaces/blocks";
import { Field } from "app/interfaces/fields";
import { ImageBoxBlock } from "app/interfaces/image";
import { ContentBoxSettingsService } from "app/main/pages/general-editor-tabs/content-box-settings/content-box-setting.service";
import { ImageType } from "app/main/pages/general-modals/user-gallery/user-galary.dto";
import { IBrandingDto } from "app/main/pages/settings/branding/branding.dto";
import { SigningServiceHttp } from "../signing-service";

@Injectable({
    providedIn: 'root'
})

export class RefreshFileService {
    private renderer: Renderer2;
    constructor(
        private globalService: GlobalService,
        private rendererFactory: RendererFactory2,
        private _signingPageService$: SigningServiceHttp,
        private contentBoxSettingsService: ContentBoxSettingsService,
        private _translocoService: TranslocoService
    ) {
        this.renderer = rendererFactory.createRenderer(null, null);
    }


    /**
    * Refresh expire images url or expire attachments
    * @param field_datas 
    * @param recipientRoleId 
    * @returns 
    */
    async refreshDocumentFiles(field_datas, recipientRoleId: number, roleId: number = 0, isEForm: boolean = false , branding: IBrandingDto) {
        if (typeof field_datas == 'string') field_datas = JSON.parse(field_datas);
        if (field_datas?.blocks) {
            for (const key in field_datas['blocks']) {
                if (Object.prototype.hasOwnProperty.call(field_datas['blocks'], key)) {
                    const block: Block = field_datas['blocks'][key];
                    const uuid = block.uuid;
                    if (block?.type == 'image' && Object.prototype.hasOwnProperty.call(block, 'file')) {
                        if (block?.file) {
                            if(block.file.indexOf('brands') > -1 && branding){
                                if(block?.image_type === ImageType.BrandingLogo){
                                    block.file = branding?.logo;
                                }else if(block?.image_type === ImageType.BrandingSecondaryLogo){
                                    block.file = branding?.secondary_logo;
                                }
                            }
                            const imageDom = document.querySelector(`.image-box-inner[uuid="${uuid}"] img`);
                            this.refreshImage(block.file, recipientRoleId, roleId, isEForm).subscribe({
                                next: (image: Blob) => {
                                    if (imageDom && image && block?.file) {
                                        const url = window.URL.createObjectURL(image);
                                        this.renderer.setAttribute(imageDom, 'src', url);
                                        this.globalService.updateBlock(uuid, { file: block.file });
                                        this.onImageLoad(url, uuid);
                                    }
                                }
                            });
                        }
                    }else if (block?.type == 'attachment' && block?.files) {
                        block?.files.forEach((file) => {
                            let attachmentDom = document.querySelector(`a[temp_uuid="${file.uuid}"]`);
                            if (!attachmentDom) {
                                attachmentDom = document.querySelector(`a[uuid="${file.uuid}"]`);
                            }
                            if (!attachmentDom || !file?.url) return;
                            // The e-form re-runs this on every edit; refetching would re-download and leak object URLs.
                            if (attachmentDom.getAttribute('href')?.startsWith('blob:') && attachmentDom.getAttribute('data-src') === file.url) return;
                            // The e-form calls this once per recipient role in the same tick, so one fetch per file must cover them all.
                            if (attachmentDom.getAttribute('data-loading') === file.url) return;
                            this.renderer.setAttribute(attachmentDom, 'data-loading', file.url);
                            this.refreshImage(file.url, recipientRoleId, roleId, isEForm).subscribe({
                                next: (attachment: Blob) => {
                                    if (attachmentDom.getAttribute('data-loading') !== file.url) return;
                                    this.renderer.removeAttribute(attachmentDom, 'data-loading');
                                    if (!attachment) return;
                                    const previousHref = attachmentDom.getAttribute('href');
                                    if (previousHref?.startsWith('blob:')) {
                                        window.URL.revokeObjectURL(previousHref);
                                    }
                                    const inert = this.inertBlob(attachment);
                                    this.renderer.setAttribute(attachmentDom, 'href', window.URL.createObjectURL(inert));
                                    this.renderer.setAttribute(attachmentDom, 'data-src', file.url);
                                    // A blob URL has no file name, so a forced download would save under the blob's UUID.
                                    if (inert !== attachment) {
                                        this.renderer.setAttribute(attachmentDom, 'download', file.filename || attachmentDom.textContent?.trim() || '');
                                    } else {
                                        this.renderer.removeAttribute(attachmentDom, 'download');
                                    }
                                },
                                error: (error) => {
                                    if (attachmentDom.getAttribute('data-loading') === file.url) {
                                        this.renderer.removeAttribute(attachmentDom, 'data-loading');
                                    }
                                    // Fetched at page load, before the signer asks for it, so a toast here would be noise.
                                    console.warn(error);
                                }
                            });
                        });
                    } else if (block?.type === 'content-box') {
                        const editor = document.querySelector(`.editor${block.uuid}`);
                        if (editor) {
                            const editorContent = editor.innerHTML.trim();
                            const cleanedContent = this.checkIfEditorEmpty(editorContent);
                            if (cleanedContent === '') {
                                const editorCloset = editor.closest('.content-box-block');
                                if (editorCloset && editorCloset.querySelectorAll('.rowItem').length <= 1) {
                                    this.renderer.addClass(editorCloset, 'hidden');
                                }
                            }
                        }
                        if (block?.background_image) {
                            this.refreshImage(block?.background_image, recipientRoleId, roleId, isEForm).subscribe({
                                next: (contentBox: Blob) => {
                                    if (contentBox) {
                                        const url = window.URL.createObjectURL(contentBox);
                                        this.contentBoxSettingsService.setContentBoxImage(block.uuid, block.background_image, true, url);
                                    }
                                }
                            });
                        }
                    } else if (block?.type === 'upload-pdf' && block?.url) {
                        const pdfBlock = document.querySelector(`.pdf-block[uuid="${block.uuid}"]`);
                        if (pdfBlock) {
                            this.refreshImage(block?.url, recipientRoleId, roleId, isEForm).subscribe({
                                next: (refreshPdfUpload: Blob) => {
                                    if (refreshPdfUpload) {
                                        const domHandlersService = AppInjector.get(DomHandlersService);
                                        const url = window.URL.createObjectURL(refreshPdfUpload);
                                        this.renderer.setProperty(pdfBlock, 'style', `--pdf-background-image:url('${url}')`);
                                        domHandlersService.setPdfBlockHeight(url, block.uuid);
                                    }
                                },
                                error: () => this.globalService.toastrError(this._translocoService.translate('Toaster alert (Shareable link preview) - Failed to load PDF. Please try again later'))
                            });
                        }
                    }else if(block?.type !== 'image' && Object.prototype.hasOwnProperty.call(block, 'file')){
                        const imageDom = document.querySelector(`.image-box-inner[uuid="${key}"] img`);
                        if(block.file.indexOf('brands') > -1 && branding?.logo){
                            block.file = branding?.logo;
                        }
                        this.refreshImage(block?.file, recipientRoleId, roleId, isEForm).subscribe({
                            next: (image: Blob) => {
                                if (imageDom && image && block?.file) {
                                    const url = window.URL.createObjectURL(image);
                                    this.renderer.setAttribute(imageDom, 'src', url);
                                    const imageBlock = ImageBoxBlock.block({ uuid: key, file: block.file });
                                    this.globalService.updateBlock(key, imageBlock);
                                }
                            }
                        });
                    } else if(block?.type === 'product-table'){
                        this.refreshProductTableImages(block, recipientRoleId, roleId, isEForm);
                    }
                }
            }
        }
        for (const key in this.globalService.fields_data) {
            if (Object.prototype.hasOwnProperty.call(this.globalService.fields_data, key)) {
                const field: Field = this.globalService.fields_data[key];
                if (field.field == 'signature-field') {
                    const signatureBox = document.querySelector(`.drag-fields[uuid="${field.uuid}"]`)?.querySelector('.signature-image');
                    if (signatureBox) {
                        if (field.value) {
                            this.refreshImage(field.value, recipientRoleId, roleId, isEForm).subscribe({
                                next: (signature: Blob) => {
                                    if (signature) {
                                        const url = window.URL.createObjectURL(signature);
                                        this.renderer.setStyle(signatureBox, 'background-image', 'url(' + url + ')');
                                    }
                                }
                            })
                        }
                    }
                }
            }
        }
    }

    /**duct table
     * This function used to refresh images of pro
     * @param block 
     * @param recipientRoleId 
     * @param roleId 
     * @param isEForm 
     */
    refreshProductTableImages(block, recipientRoleId, roleId, isEForm){
        const table = document.querySelector(`.product-table-content[table-uuid="${block.uuid}"]`);
        if(table){
            const productTableImages = table.querySelectorAll('.product-img-box');
            productTableImages.forEach(imgTag => {
                const productId = imgTag.getAttribute('product-id');
                if(productId && block?.table){
                    const product = block?.table?.find(
                        p => Number(p.id) === Number(productId)
                    );
                    const imgPath = product?.images?.length
                        ? product.images[0].image_url
                        : '';
                    if(imgPath){
                        this.refreshImage(imgPath, recipientRoleId, roleId, isEForm).subscribe({
                            next: (image: Blob) => {
                                if (imgTag && image) {
                                    const url = window.URL.createObjectURL(image);
                                    this.renderer.setAttribute(imgTag, 'src', url);
                                }
                            }
                        });
                    }
                }
            });   
        }
    }


    /**
     * Getting regenerated file url
     * @param file 
     * @param recipientRole 
     * @returns 
     */
    refreshImage(file: string, recipientRole: number, roleId: number, isEForm: boolean) {
        const apiRoute = isEForm ? `eform-signing/${recipientRole}/${roleId}` : `sign-document/${recipientRole}`;
        try {
            return this._signingPageService$.get(`${apiRoute}/stream-file?path=${file}`, { responseType: 'blob' });
        } catch (error) {
            console.warn(error);
        }
    }

    // A blob URL runs in the app origin, where the signing token lives, so active content must download instead of render.
    inertBlob(blob: Blob): Blob {
        const renderable = blob.type === 'application/pdf' || (blob.type.startsWith('image/') && blob.type !== 'image/svg+xml');
        return renderable ? blob : new Blob([blob], { type: 'application/octet-stream' });
    }

    private checkIfEditorEmpty(editorContent: string) {
        return editorContent
            .replace(/<br\s*\/?>/g, '')  // Remove <br> tags
            .replace(/<p>\s*<\/p>/g, '') // Remove empty <p> tags
            .replace(/<div>\s*<\/div>/g, '') // Remove empty divs
            .trim();
    }

    private onImageLoad(imageSrc: string, uuid: string) {
        if(!imageSrc) return;
        const TEMP_IMAGE_WIDTH = 810;
        const IMAGE_LOAD_TIMEOUT_MS = 800;
        const image = new Image(TEMP_IMAGE_WIDTH);
        const imageId = `${uuid}-${this.globalService.uuid()}`;
        image.src = imageSrc;
        image.setAttribute("id", imageId);
        image.style.display = 'none';
        image.onload = () => {
            setTimeout(() => {
                const tempDiv = document.getElementById(imageId);
                this.globalService.updateTheSigningPageHeightOnImageLoad();
                if (tempDiv) {
                    this.renderer.removeChild(document.body, tempDiv);
                }
            }, IMAGE_LOAD_TIMEOUT_MS);
        };
        this.renderer.appendChild(document.body, image);
    }
}
